/**
 * services/shopsApi.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Shop management methods (user-facing).
 *
 * METHODS:
 *   fetchShops()               — fetch all shops with pagination
 *   getShops(filters)          — get shops with optional filters
 *   postShop(formData, token)  — create a new shop (multipart form)
 *   createShop(shopData)       — create a new shop (JSON)
 *
 * NOTE: Notifications (getNotifications, markNotificationRead,
 *       markAllNotificationsRead) live in messagesApi.js.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { API_BASE_URL } from './apiUtils.js';

function _getToken() {
  return (
    localStorage.getItem('authToken')  ||
    localStorage.getItem('token')      ||
    localStorage.getItem('adminToken') ||
    localStorage.getItem('admin_token') ||
    localStorage.getItem('auth_token') ||
    sessionStorage.getItem('authToken') ||
    sessionStorage.getItem('token')    ||
    sessionStorage.getItem('adminToken')
  );
}

function _adminHeaders(token) {
  const t = token || _getToken();
  return { Authorization: `Token ${t}`, 'Content-Type': 'application/json' };
}

// ── Methods — exported as a plain object, spread into ApiService in api.js ────
export const methods = {

async fetchShops() {
  try {
    const res = await fetch(`${API_BASE_URL}/shops/`);
    if (!res.ok) throw new Error(`fetchShops failed: ${res.status}`);
    const data = await res.json();
    if (Array.isArray(data)) return data;
    if (data?.results)       return data.results;
    if (data?.shops)         return data.shops;
    if (data?.data)          return data.data;
    return [];
  } catch (error) { console.error('❌ fetchShops error:', error); return []; }
},

  async getShops(filters = {}) {
  try {
    const params = new URLSearchParams(filters);
    const res = await fetch(`${API_BASE_URL}/shops/?${params}`);
    return await res.json();
  } catch (error) { console.error('getShops error:', error); throw error; }
},

  async postShop(formData, token) {
  try {
    const t = token || this._getToken();
    if (!t) return { success: false, error: 'Please log in to create a shop.' };
    const res = await fetch(`${API_BASE_URL}/shops/`, {
      method: 'POST',
      headers: { 'Authorization': `Token ${t}` },
      body: formData,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      if (res.status === 401) {
        ['token','user','isAdmin'].forEach(k => localStorage.removeItem(k));
        return { success: false, error: 'Your session has expired. Please log out and log back in.' };
      }
      return { success: false, error: err.detail || err.error || 'Failed to create shop' };
    }
    return { success: true, data: await res.json() };
  } catch (error) { return { success: false, error: error.message || 'Network error. Please try again.' }; }
},

  async createShop(shopData) {
  try {
    const token = this._getToken();
    const res = await fetch(`${API_BASE_URL}/shops/`, {
      method: 'POST',
      headers: { 'Authorization': `Token ${token}` },
      body: shopData,
    });
    return await res.json();
  } catch (error) { console.error('createShop error:', error); throw error; }
}

// ==========================================================================
// ADMIN — USER MANAGEMENT
// ==========================================================================
};